import { Provider, h, notifications } from '@debbie/cortex/dist/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import MoneyCalculator from '@debbie/iris/dist/components/MoneyCalculator.js'
import isPropValid from '@emotion/is-prop-valid'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import React from 'react'

import 'react-datepicker/dist/react-datepicker.css'

import NotificationSystem from 'react-notification-system'
import { useSelector } from 'react-redux'
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom'
import { StyleSheetManager, createGlobalStyle, styled } from 'styled-components'
import { StyledTarget } from 'styled-components/dist/types.js'

import SigningDone from './components/SigningDone.js'
import Welcome from './components/Welcome.js'
import { ENABLE_SENTRY, SENTRY_DSN } from './config.js'
import LoggedInView from './LoggedInView.js'
import UrlShortener from './pages/UrlShortener.js'

if (ENABLE_SENTRY && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // Never send IP addresses or other default PII to Sentry (GDPR).
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({
        // Mask all rendered text and input values and block all media before
        // anything leaves the browser. Do NOT disable or add unmask overrides —
        // debtor/creditor PII (names, amounts, CPR) must never reach Sentry.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Record 10% of ordinary sessions and 100% of sessions that hit an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

moment.locale()

const Wrapper = styled.div`
  * {
    font-family: 'Montserrat', sans-serif;
    box-sizing: border-box;
  }
`

function shouldForwardProp(propName: string, target: StyledTarget<'web'>) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

const X = () => {
  const isAuthTokenLoaded = useSelector((s: RootState) =>
    h.isLoaded(s.authToken),
  )

  return (
    <StyleSheetManager
      enableVendorPrefixes
      shouldForwardProp={shouldForwardProp}
    >
      <Router>
        <Style />
        <Wrapper>
          <Switch>
            <Route path="/r/:id">
              <UrlShortener />
            </Route>
            <Route path="/signed">
              <SigningDone signed={true} />
            </Route>
            <Route path="/declined-signing">
              <SigningDone signed={false} />
            </Route>
            <Route path="/">
              <Welcome hide={isAuthTokenLoaded} />
              {isAuthTokenLoaded && <LoggedInView />}
            </Route>
          </Switch>
        </Wrapper>
        <NotificationSystem
          ref={(r) => notifications.attachNotificationHandler(r)}
        />
        <MoneyCalculator />
      </Router>
    </StyleSheetManager>
  )
}

const App = () => (
  <Provider>
    <X />
  </Provider>
)

export default App

const Style = createGlobalStyle`
  body {
    margin: 0;
    font-family: 'Montserrat',sans-serif;
    overflow: hidden;
    background-color: #F5F9FB;
  }

  a:focus {
    text-decoration: underline !important;
    outline: none;
  }

  * {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }
  *:before,
  *:after {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  .__react_component_tooltip {
    max-width: 300px;
  }

  .react-datepicker__time-list {
    padding: 0;
  }

  .react-datepicker-wrapper {
    width: 70%;
  }

  .react-datepicker__input-container {
    display: block;
  }

  .react-datepicker__input-container input {
    width: 100%;
  }
`
